import { groupIdentify, identify, Identify, init, setGroup, track } from "@amplitude/analytics-browser";
import { forEach } from "lodash";
// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import { AmplitudeConfig } from "~/app/reducers/common";
import { LSHelper } from "~/EasyProjectsCommon/LSHelper";
import { LicenseFileType } from "~/Redux/License/types";

type DelayedEvent = { event: string; properties: any };

/**
 * Indicates if Amplitude was initialized.
 */
let isInitialized = false;

/**
 * Events that were tracked before the amplitude initialization
 */
let delayedEvents: DelayedEvent[] = [];

const keyTimeOut = "debug-amplitude-timeout";
const debugAmplitudeTimeout: number = LSHelper.getJSON(keyTimeOut);
export const amplitudeSessionTimeout = debugAmplitudeTimeout || 30 * 60 * 1000; // 30 minutes

export const trackAmplitudeEvent = (event: string, properties?: any) => {
    if (isInitialized) {
        const alias = ep.settings.accountName;
        track(event, { ...properties, alias });
        return;
    }

    // we don't want to track with amplitude more than 100 delayed events in a row
    if (delayedEvents.length < 100) {
        delayedEvents.push({ event, properties });
    }
};

export const initAmplitude = ({
    configuration = null as AmplitudeConfig,
    isBirdView = false,
    license = null as LicenseFileType,
    alias = null as string,
    isSelfServe = false,
    isTrial = false,
    customizations = null as string[],
    spacesCount = null as number,
}) => {
    if (isInitialized) {
        // we could re-init app after on-boarding (set or not demo data)
        return;
    }

    init(configuration.token, configuration.userId, {
        // the default limit is 5 chars, but we use following format for
        // user id "{AccountId}:{UserId}", that min value is 3 chars
        minIdLength: 3,
        sessionTimeout: amplitudeSessionTimeout,
    });

    isInitialized = true;

    identifyAmplitudeUser({
        name: configuration.name,
        email: configuration.email,
        "user type": configuration.user_type,
        company: license.Company,
        alias,
        "is trial user": isTrial,
        "is self-serve": isSelfServe,
    });

    const companyProperty = new Identify();
    companyProperty.set("name", license.Company);
    companyProperty.set("id", configuration.company.companyId);
    companyProperty.set("alias", alias);
    companyProperty.set("is trial", isTrial);
    companyProperty.set("is self-serve", isSelfServe);
    companyProperty.set("customizations", customizations);
    companyProperty.set("is Birdview", isBirdView);
    companyProperty.set("licence", license.LicensePlan);
    companyProperty.set("licenses", license.MaxUsersCount);

    // We won't override spacesCount with incorrect value in case spacesCount request fails (spaceCount = undefined).
    if (spacesCount) {
        companyProperty.set("spaces", spacesCount);
    }

    groupIdentify("companyId", configuration.company.companyId, companyProperty);
    groupIdentify("company", alias, companyProperty);

    setGroup("companyId", configuration.company.companyId);
    setGroup("company", alias);

    // eslint-disable-next-line no-restricted-syntax
    delayedEvents.forEach(({ event, properties }) => trackAmplitudeEvent(event, properties));

    delayedEvents = null;
};

export const identifyAmplitudeUser = (userProperties: any) => {
    // do nothing if amplitude is not enabled for current account (we always call this function
    // from user onboarding with selected team functions, even if
    // current account is the test one (don't use in stats) )
    if (isInitialized) {
        const userProperty = new Identify();
        forEach(userProperties, (value, key) => userProperty.set(key, value));
        identify(userProperty);
    }
};
